import { onSnapshot, collection, doc, type Unsubscribe } from 'firebase/firestore';
import { getFirestoreInstance } from './firestore';
import type { Gig, Call, Position, Booking } from '@gigdriver/domain';
import { fromFirestore } from './serde';

export interface GigDetailHandlers {
  gig: (gig: Gig | null) => void;
  calls: (calls: Call[]) => void;
  positions: (positions: Position[]) => void;
  bookings: (positionId: string, bookings: Booking[]) => void;
  error?: (context: string, err: unknown) => void;
}

export interface WatchGigDetailOptions {
  // Skip the per-position bookings listeners entirely. Needed for a `staff`
  // viewer: firestore.rules only lets them read a booking that is their OWN
  // (resource.data.userId == their uid), and an unfiltered `list` on a
  // position's bookings subcollection can't be proven against that
  // per-document rule — Firestore denies the whole listener outright rather
  // than filtering, which otherwise surfaces as a permission-denied error on
  // every position. Staff have no use for the roster/rates this would show
  // anyway (that's what "My Offers -> Open Positions" is for), so skip it.
  skipBookings?: boolean;
}

// Real-time subscription for one gig's detail view: the gig doc, its calls,
// its positions (a flat, gig-level collection — a Position is no longer a
// child of one Call), and the bookings under each position. Returns a
// single teardown function that stops every listener in the tree (including
// the dynamic per-position bookings ones). Safe to call once per mounted
// view.
export function watchGigDetail(
  orgId: string,
  gigId: string,
  handlers: GigDetailHandlers,
  options: WatchGigDetailOptions = {},
): () => void {
  const db = getFirestoreInstance();
  const base = ['organizations', orgId, 'gigs', gigId] as const;
  const onErr = (ctx: string) => (err: unknown) => handlers.error?.(ctx, err);

  const bookingUnsubs = new Map<string, Unsubscribe>(); // positionId -> unsub

  function stopBookings(positionId: string) {
    bookingUnsubs.get(positionId)?.();
    bookingUnsubs.delete(positionId);
    // The listener is gone, so it will never deliver a final "now empty"
    // snapshot — tell the consumer directly, or its bookings Map keeps
    // whatever was last received (e.g. after the position is deleted).
    handlers.bookings(positionId, []);
  }

  function watchBookings(positionId: string) {
    if (options.skipBookings) return;
    if (bookingUnsubs.has(positionId)) return;
    bookingUnsubs.set(
      positionId,
      onSnapshot(
        collection(db, ...base, 'positions', positionId, 'bookings'),
        snap => handlers.bookings(positionId, snap.docs.map(d => fromFirestore<Booking>(d.data()))),
        onErr('bookings'),
      ),
    );
  }

  const gigUnsub = onSnapshot(
    doc(db, ...base),
    snap => handlers.gig(snap.exists() ? fromFirestore<Gig>(snap.data()) : null),
    onErr('gig'),
  );

  const callsUnsub = onSnapshot(
    collection(db, ...base, 'calls'),
    snap => handlers.calls(snap.docs.map(d => fromFirestore<Call>(d.data()))),
    onErr('calls'),
  );

  const positionsUnsub = onSnapshot(
    collection(db, ...base, 'positions'),
    snap => {
      const current = new Set(snap.docs.map(d => d.id));
      for (const positionId of [...bookingUnsubs.keys()]) {
        if (!current.has(positionId)) stopBookings(positionId);
      }
      handlers.positions(snap.docs.map(d => fromFirestore<Position>(d.data())));
      current.forEach(watchBookings);
    },
    onErr('positions'),
  );

  return () => {
    gigUnsub();
    callsUnsub();
    positionsUnsub();
    bookingUnsubs.forEach(u => u());
    bookingUnsubs.clear();
  };
}
